import { lazy, type ComponentType } from "react";

export interface ArticleMeta {
  slug: string;
  category: string;
  title: string;
  description: string;
  /**
   * Optional hidden SEO overrides. When present they drive the <title> tag and
   * meta description only; the visible H1 always uses `title` and the visible
   * lede always uses `description`. Mirrors the CATEGORIES seoTitle/seoDescription
   * pattern and is duplicated in scripts/prerender.mts (dual-source meta).
   */
  seoTitle?: string;
  seoDescription?: string;
  publishedAt: string;
  updatedAt: string;
  reviewedAt: string;
  sourcesCheckedAt?: string;
  /** Evidence label set by the medical reviewer. Rendered only when present. */
  evidenceLevel?: "strong" | "emerging" | "educational";
  readingTime: number;
  hero?: { src: string; alt: string; credit?: string };
  keywords?: string[];
  faq?: { q: string; a: string }[];
  related?: string[];
  network_topics?: string[];
  draft?: boolean;
  medicalCondition?: {
    name: string;
    alternateNames?: string[];
    signOrSymptom?: string[];
  };
}

export interface Article extends ArticleMeta {
  Body: ComponentType;
}

// Frontmatter is pre-extracted at build time by `scripts/generate-content-index.mjs`
// into a JSON file. This guarantees zero MDX content lands in the eager bundle , 
// article bodies load on demand via the template-literal dynamic import below.
import articlesIndex from "./__generated__/articles.json";

const ARTICLES: Record<string, Article> = {};
for (const fm of articlesIndex as ArticleMeta[]) {
  if (!fm || !fm.slug || !fm.category) continue;
  if (fm.draft) continue;
  if (!fm.title || typeof fm.readingTime !== "number") continue;
  const category = fm.category;
  const slug = fm.slug;
  const Body = lazy(
    () => import(`../content/${category}/${slug}.mdx`) as Promise<{
      default: ComponentType;
    }>,
  );
  ARTICLES[`${category}/${slug}`] = { ...fm, Body };
}

export function getArticle(category: string, slug: string): Article | undefined {
  return ARTICLES[`${category}/${slug}`];
}

export function getArticlesByCategory(category: string): Article[] {
  return Object.values(ARTICLES)
    .filter((a) => a.category === category)
    .sort(
      (a, b) =>
        b.publishedAt.localeCompare(a.publishedAt) ||
        a.title.localeCompare(b.title),
    );
}

export function getAllArticles(): Article[] {
  return Object.values(ARTICLES).sort((a, b) =>
    b.publishedAt.localeCompare(a.publishedAt),
  );
}
